import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDefaults, calculateAppraisal, createScenario, getScenario, updateScenario, downloadReport, downloadCashflowPdf, downloadExcel, getCashflow, listTemplates, getTemplate, fmt, fmtFull, fmtPct, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Calculator, Save, Loader2, Plus, Trash2, Building2, Banknote, Landmark, PiggyBank, TrendingUp, FileDown, BarChart3, Home, Layers, GraduationCap, ArrowRight, FileSpreadsheet, Pencil } from "lucide-react";

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

const NUM = (v) => (v === "" || v === null || v === undefined) ? 0 : Number(v);

export default function Appraisal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [location, setLocation] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [exportingCf, setExportingCf] = useState(false);
  const [exportingXl, setExportingXl] = useState(false);
  const [scenarioId, setScenarioId] = useState(id || null);
  const [formTab, setFormTab] = useState("project");
  const [resultTab, setResultTab] = useState("developer");
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(null);

  useEffect(() => {
    if (id) {
      getScenario(id).then((s) => {
        setInputs(s.inputs);
        setProjectName(s.project_name);
        setSiteAddress(s.site_address || "");
        setLocation(s.location);
        setResults(s.results);
        setScenarioId(s.id);
      }).catch(() => toast.error("Scenario not found"));
    } else {
      getDefaults().then((d) => setInputs(d)).catch(console.error);
      listTemplates().then(setTemplates).catch(console.error);
    }
  }, [id]);

  const applyTemplate = async (templateId) => {
    setTemplateLoading(templateId);
    try {
      const tmpl = await getTemplate(templateId);
      setInputs(tmpl.inputs);
      setProjectName(tmpl.name);
      setResults(null);
      setScenarioId(null);
      toast.success(`Template "${tmpl.name}" loaded`);
    } catch (e) {
      toast.error("Failed to load template");
    } finally {
      setTemplateLoading(null);
    }
  };

  if (!inputs) return <div style={{ padding: 40 }}>Loading...</div>;

  const updateField = (path, value) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!isNaN(keys[i])) keys[i] = parseInt(keys[i]);
        obj = obj[keys[i]];
      }
      const last = keys[keys.length - 1];
      obj[last] = value;
      return copy;
    });
  };

  const updateBlockField = (blockIdx, field, type, value) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.blocks[blockIdx][field][type] = NUM(value);
      return copy;
    });
  };

  const addCostItem = (category) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.costs[category].push({ name: "New Item", amount: 0 });
      return copy;
    });
  };

  const removeCostItem = (category, idx) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.costs[category].splice(idx, 1);
      return copy;
    });
  };

  const addFunderCost = () => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.funder_costs.push({ name: "New Item", amount: 0 });
      return copy;
    });
  };

  const removeFunderCost = (idx) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.funder_costs.splice(idx, 1);
      return copy;
    });
  };

  const addBlock = () => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const ut = copy.unit_types || ["Studio", "1B2P", "2B4P", "3B4P"];
      const emptyUnits = {};
      const emptyRents = {};
      const emptySales = {};
      ut.forEach((t) => { emptyUnits[t] = 0; emptyRents[t] = 0; emptySales[t] = 0; });
      const newBlock = { name: `Block ${copy.blocks.length + 1}`, units: emptyUnits, rents_pcm: emptyRents };
      if (copy.valuation_mode === "sales") newBlock.sales_values = emptySales;
      copy.blocks.push(newBlock);
      return copy;
    });
  };

  const removeBlock = (idx) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      if (copy.blocks.length <= 1) return copy;
      copy.blocks.splice(idx, 1);
      return copy;
    });
  };

  const renameBlock = (idx, name) => {
    setInputs((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.blocks[idx].name = name;
      return copy;
    });
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const res = await calculateAppraisal(inputs);
      setResults(res);
      toast.success("Appraisal calculated");
    } catch (e) {
      toast.error("Calculation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { project_name: projectName || "Untitled Scenario", site_address: siteAddress, location, inputs };
      if (scenarioId) {
        await updateScenario(scenarioId, payload);
        toast.success("Scenario updated");
      } else {
        const res = await createScenario(payload);
        setScenarioId(res.id);
        setResults(res.results);
        navigate(`/appraisal/${res.id}`, { replace: true });
        toast.success("Scenario saved");
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!scenarioId) {
      toast.error("Save the scenario first to export PDF");
      return;
    }
    setExporting(true);
    try {
      await downloadReport(scenarioId);
      toast.success("PDF report downloaded");
    } catch (e) {
      toast.error("Export failed: " + (e.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  };

  const loadCashflow = async () => {
    if (!scenarioId) return;
    setCashflowLoading(true);
    try {
      const data = await getCashflow(scenarioId);
      setCashflowData(data);
    } catch (e) {
      toast.error("Failed to load cashflow");
    } finally {
      setCashflowLoading(false);
    }
  };

  const handleExportCashflowPDF = async () => {
    if (!scenarioId) {
      toast.error("Save the scenario first");
      return;
    }
    setExportingCf(true);
    try {
      await downloadCashflowPdf(scenarioId);
      toast.success("Cashflow PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExportingCf(false);
    }
  };

  const handleExportExcel = async () => {
    if (!scenarioId) {
      toast.error("Save the scenario first");
      return;
    }
    setExportingXl(true);
    try {
      await downloadExcel(scenarioId);
      toast.success("Excel file downloaded");
    } catch (e) {
      toast.error("Excel export failed");
    } finally {
      setExportingXl(false);
    }
  };

  const ut = inputs.unit_types || ["Studio", "1B2P", "2B4P", "3B4P"];
  const blocks = inputs.blocks || [];
  const isSalesMode = inputs.valuation_mode === "sales";

  return (
    <div className="page-enter" style={{ padding: "32px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 28 }} data-testid="appraisal-title">
            {scenarioId ? "Edit Appraisal" : "New Appraisal"}
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginTop: 4 }}>
            Configure inputs and calculate development returns
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={handleCalculate} disabled={loading} style={{ background: "hsl(var(--accent))", color: "white" }} data-testid="calculate-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
            <span style={{ marginLeft: 8 }}>Calculate</span>
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            <span style={{ marginLeft: 8 }}>Save</span>
          </Button>
          {scenarioId && results && (
            <Button onClick={handleExportPDF} disabled={exporting} variant="outline" data-testid="export-pdf-btn">
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
              <span style={{ marginLeft: 8 }}>Appraisal PDF</span>
            </Button>
          )}
          {scenarioId && results && (
            <Button onClick={handleExportCashflowPDF} disabled={exportingCf} variant="outline" data-testid="export-cashflow-pdf-btn">
              {exportingCf ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />}
              <span style={{ marginLeft: 8 }}>Cashflow PDF</span>
            </Button>
          )}
          {scenarioId && results && (
            <Button onClick={handleExportExcel} disabled={exportingXl} variant="outline" data-testid="export-excel-btn" style={{ borderColor: "hsl(142 60% 40%)", color: "hsl(142 60% 30%)" }}>
              {exportingXl ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
              <span style={{ marginLeft: 8 }}>Excel</span>
            </Button>
          )}
        </div>
      </div>

      {/* TEMPLATE SELECTOR - only for new appraisals */}
      {!id && templates.length > 0 && (
        <div style={{ marginBottom: 24 }} data-testid="template-selector">
          <div style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", marginBottom: 12 }}>
            Start from a template
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {templates.map((t) => {
              const Icon = t.icon === "building" ? Building2 : t.icon === "home" ? Home : t.icon === "layers" ? Layers : t.icon === "banknote" ? Banknote : GraduationCap;
              const isLoading = templateLoading === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t.id)}
                  disabled={!!templateLoading}
                  data-testid={`template-${t.id}`}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8,
                    padding: "16px 18px", borderRadius: 10,
                    border: "1px solid hsl(var(--border))",
                    background: "hsl(var(--card))",
                    cursor: isLoading ? "wait" : "pointer",
                    textAlign: "left",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "hsl(var(--foreground))"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "hsl(var(--border))"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                    {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Icon size={18} style={{ color: "hsl(var(--accent))" }} />}
                    <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "Manrope" }}>{t.name}</span>
                  </div>
                  <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>{t.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* INPUT FORM */}
      <Tabs value={formTab} onValueChange={setFormTab} data-testid="form-tabs">
        <TabsList style={{ marginBottom: 20 }}>
          <TabsTrigger value="project">Project</TabsTrigger>
          <TabsTrigger value="units">Unit Mix</TabsTrigger>
          <TabsTrigger value="rents">{isSalesMode ? "Sales Values" : "Rents & Yields"}</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="finance">Finance</TabsTrigger>
        </TabsList>

        {/* Project & Timing */}
        <TabsContent value="project">
          <Card>
            <CardContent style={{ padding: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <Label>Project Name</Label>
                  <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. Bristol Waterfront" className="mt-1" data-testid="project-name-input" />
                </div>
                <div>
                  <Label>Location / Postcode</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. BS1 6QH" className="mt-1" data-testid="location-input" />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <Label>Site Address</Label>
                <Input value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} placeholder="e.g. 123 Harbour Road, Bristol, BS1 6QH" className="mt-1" data-testid="site-address-input" />
              </div>
              <Separator className="my-6" />
              <div className="section-header">Timing</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {[
                  { label: "Financial Close", key: "timing.financial_close" },
                  { label: "Practical Completion", key: "timing.pc_date" },
                  { label: "Stabilisation", key: "timing.stabilisation_date" },
                  { label: "Exit / Sale", key: "timing.exit_date" },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    <Input
                      type="date"
                      value={key.split(".").reduce((o, k) => o?.[k], inputs) || ""}
                      onChange={(e) => updateField(key, e.target.value)}
                      className="mt-1"
                      style={{ fontFamily: "JetBrains Mono", fontSize: 13 }}
                      data-testid={`input-${key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Mix */}
        <TabsContent value="units">
          <Card>
            <CardContent style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div className="section-header" style={{ marginBottom: 0 }}>Unit Mix by Block</div>
                <Button variant="outline" size="sm" onClick={addBlock} data-testid="add-block-btn" style={{ gap: 4 }}>
                  <Plus size={14} /> Add Block
                </Button>
              </div>
              <Table data-testid="unit-mix-table">
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ width: 180 }}>Block</TableHead>
                    {ut.map((t) => <TableHead key={t} style={{ textAlign: "right" }}>{t}</TableHead>)}
                    <TableHead style={{ textAlign: "right" }}>Total</TableHead>
                    <TableHead style={{ width: 40 }} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blocks.map((b, bi) => (
                    <TableRow key={bi}>
                      <TableCell>
                        <input
                          type="text"
                          className="data-input"
                          style={{ width: "100%", fontWeight: 600, fontSize: 13 }}
                          value={b.name}
                          onChange={(e) => renameBlock(bi, e.target.value)}
                          data-testid={`block-name-${bi}`}
                        />
                      </TableCell>
                      {ut.map((t) => (
                        <TableCell key={t} style={{ textAlign: "right" }}>
                          <input
                            type="number"
                            className="data-input"
                            style={{ width: 80, textAlign: "right" }}
                            value={b.units?.[t] ?? 0}
                            onChange={(e) => updateBlockField(bi, "units", t, e.target.value)}
                            data-testid={`units-${bi}-${t}`}
                          />
                        </TableCell>
                      ))}
                      <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                        {ut.reduce((s, t) => s + (b.units?.[t] || 0), 0)}
                      </TableCell>
                      <TableCell>
                        {blocks.length > 1 && (
                          <button
                            onClick={() => removeBlock(bi)}
                            data-testid={`remove-block-${bi}`}
                            title="Remove block"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 4, color: "#b91c1c" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Total</TableCell>
                    {ut.map((t) => (
                      <TableCell key={t} className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                        {blocks.reduce((s, b) => s + (b.units?.[t] || 0), 0)}
                      </TableCell>
                    ))}
                    <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                      {blocks.reduce((s, b) => s + ut.reduce((ss, t) => ss + (b.units?.[t] || 0), 0), 0)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>

              <Separator className="my-6" />
              <div className="section-header">Unit Areas (NIA sq ft)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {ut.map((t) => (
                  <div key={t}>
                    <Label>{t}</Label>
                    <input
                      type="number"
                      className="data-input mt-1"
                      value={inputs.unit_areas?.[t] ?? 0}
                      onChange={(e) => updateField(`unit_areas.${t}`, NUM(e.target.value))}
                      data-testid={`area-${t}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rents & Yields OR Sales Values */}
        <TabsContent value="rents">
          {isSalesMode ? (
            <Card>
              <CardContent style={{ padding: 24 }}>
                <div className="section-header">Sales Values per Unit by Block</div>
                <Table data-testid="sales-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ width: 180 }}>Block</TableHead>
                      {ut.map((t) => <TableHead key={t} style={{ textAlign: "right" }}>{t}</TableHead>)}
                      <TableHead style={{ textAlign: "right" }}>Total GDV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blocks.map((b, bi) => (
                      <TableRow key={bi}>
                        <TableCell style={{ fontWeight: 600 }}>{b.name}</TableCell>
                        {ut.map((t) => (
                          <TableCell key={t} style={{ textAlign: "right" }}>
                            <input
                              type="number"
                              className="data-input"
                              style={{ width: 110, textAlign: "right" }}
                              value={b.sales_values?.[t] ?? 0}
                              onChange={(e) => {
                                setInputs((prev) => {
                                  const copy = JSON.parse(JSON.stringify(prev));
                                  if (!copy.blocks[bi].sales_values) copy.blocks[bi].sales_values = {};
                                  copy.blocks[bi].sales_values[t] = NUM(e.target.value);
                                  return copy;
                                });
                              }}
                              data-testid={`sales-${bi}-${t}`}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                          {fmtFull(ut.reduce((s, t) => s + (b.units?.[t] || 0) * (b.sales_values?.[t] || 0), 0))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell style={{ fontWeight: 700 }}>Total GDV</TableCell>
                      {ut.map((t) => (
                        <TableCell key={t} className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                          {fmtFull(blocks.reduce((s, b) => s + (b.units?.[t] || 0) * (b.sales_values?.[t] || 0), 0))}
                        </TableCell>
                      ))}
                      <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                        {fmtFull(blocks.reduce((s, b) => s + ut.reduce((ss, t) => ss + (b.units?.[t] || 0) * (b.sales_values?.[t] || 0), 0), 0))}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>

                <Separator className="my-6" />
                <div className="section-header">Purchaser's Costs</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                  <div>
                    <Label>Purchaser's Costs (%)</Label>
                    <input
                      type="number"
                      step="0.01"
                      className="data-input mt-1"
                      value={inputs.yields?.purchasers_costs ?? 0}
                      onChange={(e) => updateField("yields.purchasers_costs", NUM(e.target.value))}
                      data-testid="input-yields.purchasers_costs"
                    />
                  </div>
                </div>

                <Separator className="my-6" />
                <div className="section-header">Parking</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                  <div>
                    <Label>Parking Spaces</Label>
                    <input type="number" className="data-input mt-1" value={inputs.parking?.spaces ?? 0}
                      onChange={(e) => updateField("parking.spaces", NUM(e.target.value))} data-testid="input-parking-spaces-sales" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
          <Card>
            <CardContent style={{ padding: 24 }}>
              <div className="section-header">Monthly Rents (PCM) by Block</div>
              <Table data-testid="rents-table">
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ width: 120 }}>Block</TableHead>
                    {ut.map((t) => <TableHead key={t} style={{ textAlign: "right" }}>{t}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blocks.map((b, bi) => (
                    <TableRow key={bi}>
                      <TableCell style={{ fontWeight: 600 }}>{b.name}</TableCell>
                      {ut.map((t) => (
                        <TableCell key={t} style={{ textAlign: "right" }}>
                          <input
                            type="number"
                            className="data-input"
                            style={{ width: 100, textAlign: "right" }}
                            value={b.rents_pcm?.[t] ?? 0}
                            onChange={(e) => updateBlockField(bi, "rents_pcm", t, e.target.value)}
                            data-testid={`rent-${bi}-${t}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Separator className="my-6" />
              <div className="section-header">Yields & Rates</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {[
                  { label: "Residential NIY (%)", key: "yields.residential" },
                  { label: "Commercial NIY (%)", key: "yields.commercial" },
                  { label: "Purchaser's Costs (%)", key: "yields.purchasers_costs" },
                  { label: "Opex Ratio (%)", key: "yields.opex_ratio" },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    <input
                      type="number"
                      step="0.01"
                      className="data-input mt-1"
                      value={key.split(".").reduce((o, k) => o?.[k], inputs) ?? 0}
                      onChange={(e) => updateField(key, NUM(e.target.value))}
                      data-testid={`input-${key}`}
                    />
                  </div>
                ))}
              </div>

              <Separator className="my-6" />
              <div className="section-header">Commercial & Parking</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                <div>
                  <Label>Commercial Area (sq ft)</Label>
                  <input type="number" className="data-input mt-1" value={inputs.commercial?.area_sqft ?? 0}
                    onChange={(e) => updateField("commercial.area_sqft", NUM(e.target.value))} data-testid="input-comm-area" />
                </div>
                <div>
                  <Label>Commercial Rent (psf)</Label>
                  <input type="number" step="0.5" className="data-input mt-1" value={inputs.commercial?.rent_psf ?? 0}
                    onChange={(e) => updateField("commercial.rent_psf", NUM(e.target.value))} data-testid="input-comm-rent" />
                </div>
                <div>
                  <Label>Parking Spaces</Label>
                  <input type="number" className="data-input mt-1" value={inputs.parking?.spaces ?? 0}
                    onChange={(e) => updateField("parking.spaces", NUM(e.target.value))} data-testid="input-parking-spaces" />
                </div>
                <div>
                  <Label>Parking Rent /space /pa</Label>
                  <input type="number" className="data-input mt-1" value={inputs.parking?.rent_per_space_pa ?? 0}
                    onChange={(e) => updateField("parking.rent_per_space_pa", NUM(e.target.value))} data-testid="input-parking-rent" />
                </div>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Costs */}
        <TabsContent value="costs">
          <div style={{ display: "grid", gap: 16 }}>
            {[
              { title: "Land Costs", key: "land", icon: Landmark },
              { title: "Construction Costs", key: "construction", icon: Building2 },
              { title: "Soft Costs", key: "soft", icon: Banknote },
            ].map(({ title, key, icon: Icon }) => (
              <Card key={key}>
                <CardHeader style={{ paddingBottom: 12 }}>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon size={16} style={{ color: "hsl(var(--accent))" }} /> {title}
                    </span>
                    <span className="data-cell" style={{ fontSize: 15 }}>
                      {fmtFull(inputs.costs?.[key]?.reduce((s, i) => s + (i.amount || 0), 0))}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {inputs.costs?.[key]?.map((item, idx) => (
                    <div key={idx} className="cost-row">
                      <input
                        type="text"
                        className="data-input"
                        style={{ flex: 1, fontFamily: "IBM Plex Sans", fontSize: 13 }}
                        value={item.name}
                        onChange={(e) => {
                          const copy = JSON.parse(JSON.stringify(inputs));
                          copy.costs[key][idx].name = e.target.value;
                          setInputs(copy);
                        }}
                        data-testid={`cost-name-${key}-${idx}`}
                      />
                      <input
                        type="number"
                        className="data-input"
                        style={{ width: 160, textAlign: "right" }}
                        value={item.amount}
                        onChange={(e) => {
                          const copy = JSON.parse(JSON.stringify(inputs));
                          copy.costs[key][idx].amount = NUM(e.target.value);
                          setInputs(copy);
                        }}
                        data-testid={`cost-amount-${key}-${idx}`}
                      />
                      <button className="cost-remove" onClick={() => removeCostItem(key, idx)} data-testid={`cost-remove-${key}-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => addCostItem(key)} style={{ marginTop: 8 }} data-testid={`add-cost-${key}`}>
                    <Plus size={14} style={{ marginRight: 4 }} /> Add Item
                  </Button>
                </CardContent>
              </Card>
            ))}

            <Card>
              <CardHeader style={{ paddingBottom: 12 }}>
                <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <PiggyBank size={16} style={{ color: "hsl(var(--accent))" }} /> Funder Additional Costs
                  </span>
                  <span className="data-cell" style={{ fontSize: 15 }}>
                    {fmtFull(inputs.funder_costs?.reduce((s, i) => s + (i.amount || 0), 0))}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {inputs.funder_costs?.map((item, idx) => (
                  <div key={idx} className="cost-row">
                    <input type="text" className="data-input" style={{ flex: 1, fontFamily: "IBM Plex Sans", fontSize: 13 }}
                      value={item.name} onChange={(e) => { const c = JSON.parse(JSON.stringify(inputs)); c.funder_costs[idx].name = e.target.value; setInputs(c); }}
                      data-testid={`funder-cost-name-${idx}`} />
                    <input type="number" className="data-input" style={{ width: 160, textAlign: "right" }}
                      value={item.amount} onChange={(e) => { const c = JSON.parse(JSON.stringify(inputs)); c.funder_costs[idx].amount = NUM(e.target.value); setInputs(c); }}
                      data-testid={`funder-cost-amount-${idx}`} />
                    <button className="cost-remove" onClick={() => removeFunderCost(idx)}><Trash2 size={14} /></button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={addFunderCost} style={{ marginTop: 8 }} data-testid="add-funder-cost">
                  <Plus size={14} style={{ marginRight: 4 }} /> Add Item
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Finance */}
        <TabsContent value="finance">
          <Card>
            <CardContent style={{ padding: 24 }}>
              <div className="section-header">Finance Assumptions</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                {[
                  { label: "LTC (%)", key: "finance.ltc" },
                  { label: "Interest Rate (%)", key: "finance.interest_rate" },
                  { label: "Origination Fee (%)", key: "finance.origination_fee" },
                  { label: "Exit Fee (%)", key: "finance.exit_fee" },
                  { label: "Dev Duration (months)", key: "finance.dev_duration_months" },
                  { label: "Stabilisation (months)", key: "finance.stabilisation_months" },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    <input type="number" step="0.1" className="data-input mt-1"
                      value={key.split(".").reduce((o, k) => o?.[k], inputs) ?? 0}
                      onChange={(e) => updateField(key, NUM(e.target.value))}
                      data-testid={`input-${key}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* RESULTS */}
      {results && (
        <div style={{ marginTop: 32 }} className="animate-in" data-testid="results-section">
          <Separator className="mb-6" />

          {/* KPI Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Total GDV", value: fmt(results.kpis?.total_gdv) },
              { label: "Dev Profit", value: fmt(results.kpis?.dev_profit), color: results.kpis?.dev_profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" },
              { label: "Profit on Cost", value: fmtPct(results.kpis?.dev_profit_on_cost) },
              { label: "Ungeared IRR", value: fmtPct(results.kpis?.ungeared_irr) },
            ].map((k, i) => (
              <div key={i} className="kpi-card">
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={{ fontSize: 20, color: k.color }} data-testid={`result-${k.label.toLowerCase().replace(/\s/g, "-")}`}>{k.value}</div>
              </div>
            ))}
          </div>

          <Tabs value={resultTab} onValueChange={setResultTab} data-testid="result-tabs">
            <TabsList>
              <TabsTrigger value="developer" data-testid="tab-developer">Developer Appraisal</TabsTrigger>
              <TabsTrigger value="funder" data-testid="tab-funder">Funder Appraisal</TabsTrigger>
              {scenarioId && (
                <TabsTrigger value="cashflow" data-testid="tab-cashflow" onClick={loadCashflow}>Cashflow</TabsTrigger>
              )}
            </TabsList>

            {/* Developer Appraisal */}
            <TabsContent value="developer" style={{ marginTop: 20 }}>
              <div style={{ display: "grid", gap: 16 }}>
                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>{isSalesMode ? "Residual Valuation" : "Development Value"}</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Demise</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Units</TableHead>
                          <TableHead style={{ textAlign: "right" }}>NIA (sq ft)</TableHead>
                          {isSalesMode ? (
                            <>
                              <TableHead style={{ textAlign: "right" }}>Avg Sale Price</TableHead>
                              <TableHead style={{ textAlign: "right" }}>Gross Sales Value</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead style={{ textAlign: "right" }}>Gross Rent PA</TableHead>
                              <TableHead style={{ textAlign: "right" }}>Net Rent PA</TableHead>
                              <TableHead style={{ textAlign: "right" }}>Yield</TableHead>
                              <TableHead style={{ textAlign: "right" }}>Gross CV</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {results.developer_appraisal?.blocks?.map((b) => (
                          <TableRow key={b.name}>
                            <TableCell style={{ fontWeight: 500 }}>{b.name}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{b.units}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtNum(b.nia)}</TableCell>
                            {isSalesMode ? (
                              <>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.avg_sale_price)}</TableCell>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.gross_cv)}</TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.gross_rent_pa)}</TableCell>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.net_rent_pa)}</TableCell>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{(inputs.yields?.residential || 0).toFixed(2)}%</TableCell>
                                <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(b.gross_cv)}</TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                        {!isSalesMode && (
                          <TableRow>
                            <TableCell style={{ fontWeight: 700 }}>Commercial</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>-</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtNum(results.developer_appraisal?.commercial?.area)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.developer_appraisal?.commercial?.gross_rent_pa)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.developer_appraisal?.commercial?.gross_rent_pa)}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{(inputs.yields?.commercial || 0).toFixed(2)}%</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.developer_appraisal?.commercial?.gross_cv)}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>{isSalesMode ? "Gross Sales Value" : "Gross Dev Value"}</TableCell>
                          <TableCell colSpan={isSalesMode ? 3 : 5} />
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.developer_appraisal?.total_gdv)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>{isSalesMode ? "Net Sales Value" : "Net Dev Value"}</TableCell>
                          <TableCell colSpan={isSalesMode ? 2 : 4} />
                          <TableCell className="data-cell" style={{ textAlign: "right", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
                            Less {inputs.yields?.purchasers_costs}%
                          </TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.developer_appraisal?.total_ndv)}</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Development Costs</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        {["land", "construction", "soft"].map((cat) => (
                          <TableRow key={cat}>
                            <TableCell style={{ fontWeight: 600, textTransform: "uppercase", fontSize: 13, letterSpacing: "0.03em" }}>
                              {cat === "soft" ? "Soft Costs" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                              {fmtFull(results.developer_appraisal?.costs?.[cat]?.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Total Costs (Excl. Finance)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                            {fmtFull(results.developer_appraisal?.costs?.total_excl_finance)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div className="kpi-card" style={{ borderLeftColor: results.developer_appraisal?.profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }}>
                    <div className="kpi-label">Development Profit</div>
                    <div className="kpi-value" style={{ color: results.developer_appraisal?.profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }}>
                      {fmtFull(results.developer_appraisal?.profit)}
                    </div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-label">Profit on Cost</div>
                    <div className="kpi-value">{fmtPct(results.developer_appraisal?.profit_on_cost)}</div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Funder Appraisal */}
            <TabsContent value="funder" style={{ marginTop: 20 }}>
              <div style={{ display: "grid", gap: 16 }}>
                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Funder Costs Summary</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell style={{ fontWeight: 500 }}>Development Costs (to Developer)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.development_costs)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 500 }}>Developer's Profit</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.developer_profit)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 500 }}>Funder Other Costs</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.funder_other_costs?.total)}</TableCell>
                        </TableRow>
                        <TableRow style={{ borderTop: "2px solid hsl(var(--border))" }}>
                          <TableCell style={{ fontWeight: 700 }}>Total Costs (Excl. Finance)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.funder_appraisal?.costs?.total_excl_finance)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Finance</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Loan Amount (LTC {inputs.finance?.ltc}%)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.finance?.loan_amount)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Equity</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.finance?.equity)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Interest ({inputs.finance?.interest_rate}% pa)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.finance?.interest)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Origination Fee</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.finance?.origination_fee)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Exit Fee</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right" }}>{fmtFull(results.funder_appraisal?.costs?.finance?.exit_fee)}</TableCell>
                        </TableRow>
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Total Finance</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.funder_appraisal?.costs?.finance?.total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700 }}>Total Costs (Incl. Finance)</TableCell>
                          <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>{fmtFull(results.funder_appraisal?.costs?.total_incl_finance)}</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle style={{ fontSize: 15 }}>Returns</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead style={{ width: 200 }}>Metric</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Ungeared</TableHead>
                          <TableHead style={{ textAlign: "right" }}>Geared</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[
                          { label: "Cash In", key: "cash_in", format: fmtFull },
                          { label: "Cash Out", key: "cash_out", format: fmtFull },
                          { label: "Profit", key: "profit", format: fmtFull },
                          { label: "Profit on Cost", key: "profit_on_cost", format: fmtPct },
                          { label: "IRR", key: "irr", format: fmtPct },
                          { label: "Equity Multiple", key: "equity_multiple", format: (v) => v?.toFixed(2) + "x" },
                        ].map((m) => (
                          <TableRow key={m.label}>
                            <TableCell style={{ fontWeight: 500 }}>{m.label}</TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>
                              {m.format(results.funder_appraisal?.returns?.ungeared?.[m.key])}
                            </TableCell>
                            <TableCell className="data-cell" style={{ textAlign: "right" }}>
                              {m.format(results.funder_appraisal?.returns?.geared?.[m.key])}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Cashflow */}
            <TabsContent value="cashflow" style={{ marginTop: 20 }}>
              {cashflowLoading && (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Loader2 size={24} className="animate-spin" style={{ margin: "0 auto", color: "hsl(var(--accent))" }} />
                </div>
              )}
              {cashflowData && !cashflowLoading && (
                <div style={{ display: "grid", gap: 16 }}>
                  {/* Summary KPIs */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                    {[
                      { label: "Project Duration", value: `${cashflowData.summary?.total_months} months` },
                      { label: "Peak Debt", value: fmtFull(cashflowData.summary?.peak_debt) },
                      { label: "Total Interest", value: fmtFull(cashflowData.summary?.total_interest) },
                      { label: "Peak -ve Cashflow", value: fmtFull(cashflowData.summary?.peak_negative_cashflow) },
                    ].map((k, i) => (
                      <div key={i} className="kpi-card">
                        <div className="kpi-label">{k.label}</div>
                        <div className="kpi-value" style={{ fontSize: 18 }} data-testid={`cf-kpi-${i}`}>{k.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Waterfall Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                        <BarChart3 size={16} style={{ color: "hsl(var(--accent))" }} /> Cashflow Waterfall
                      </CardTitle>
                    </CardHeader>
                    <CardContent data-testid="cashflow-chart">
                      <ResponsiveContainer width="100%" height={360}>
                        <ComposedChart data={cashflowData.cashflow} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                            tickFormatter={(v) => (v % 3 === 0 ? `M${v}` : "")}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                            tickFormatter={(v) => {
                              if (Math.abs(v) >= 1e6) return `\u00A3${(v / 1e6).toFixed(0)}m`;
                              if (Math.abs(v) >= 1e3) return `\u00A3${(v / 1e3).toFixed(0)}k`;
                              return `\u00A3${v}`;
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              fontFamily: "IBM Plex Sans", fontSize: 12,
                              borderRadius: 6, border: "1px solid hsl(var(--border))",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                            }}
                            formatter={(value, name) => [fmtFull(value), name]}
                            labelFormatter={(v) => `Month ${v}`}
                          />
                          <Legend wrapperStyle={{ fontSize: 12, fontFamily: "IBM Plex Sans" }} />
                          <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeWidth={1.5} />
                          <Bar dataKey="net_cashflow" name="Net Cashflow" radius={[2, 2, 0, 0]} maxBarSize={12}>
                            {cashflowData.cashflow.map((entry, i) => (
                              <Cell
                                key={i}
                                fill={entry.net_cashflow >= 0 ? "hsl(160, 84%, 39%)" : "hsl(0, 84%, 60%)"}
                                fillOpacity={0.7}
                              />
                            ))}
                          </Bar>
                          <Line
                            dataKey="cumulative"
                            name="Cumulative"
                            type="monotone"
                            stroke="hsl(var(--foreground))"
                            strokeWidth={2}
                            dot={false}
                          />
                          <Line
                            dataKey="debt_balance"
                            name="Debt Balance"
                            type="monotone"
                            stroke="hsl(var(--accent))"
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            dot={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Quarterly Summary Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: 15 }}>Quarterly Cashflow Summary</CardTitle>
                    </CardHeader>
                    <CardContent style={{ overflowX: "auto" }}>
                      <Table data-testid="cashflow-table">
                        <TableHeader>
                          <TableRow>
                            {["Quarter", "Land", "Construction", "Soft Costs", "Funder", "Interest", "Rental Inc.", "Net CF", "Cumulative"].map((h) => (
                              <TableHead key={h} style={{ textAlign: h === "Quarter" ? "left" : "right", fontSize: 12, whiteSpace: "nowrap" }}>{h}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            const cf = cashflowData.cashflow || [];
                            const quarters = [];
                            let q = null;
                            for (const r of cf) {
                              const qi = Math.floor(r.month / 3);
                              if (!q || q.qi !== qi) {
                                if (q) quarters.push(q);
                                q = { qi, start: r.month, end: r.month, land: 0, construction: 0, soft_costs: 0, funder_costs: 0, interest: 0, rental_income: 0, net_cashflow: 0, cumulative: r.cumulative };
                              } else {
                                q.end = r.month;
                                q.cumulative = r.cumulative;
                              }
                              q.land += r.land || 0;
                              q.construction += r.construction || 0;
                              q.soft_costs += r.soft_costs || 0;
                              q.funder_costs += r.funder_costs || 0;
                              q.interest += r.interest || 0;
                              q.rental_income += r.rental_income || 0;
                              q.net_cashflow += r.net_cashflow || 0;
                            }
                            if (q) quarters.push(q);
                            return quarters.map((qr, i) => (
                              <TableRow key={i}>
                                <TableCell style={{ fontWeight: 500, whiteSpace: "nowrap" }}>M{qr.start}-{qr.end}</TableCell>
                                {[qr.land, qr.construction, qr.soft_costs, qr.funder_costs, qr.interest, qr.rental_income].map((v, j) => (
                                  <TableCell key={j} className="data-cell" style={{ textAlign: "right", color: v > 0 ? "hsl(var(--success))" : v < 0 ? "hsl(var(--destructive))" : undefined }}>
                                    {v !== 0 ? fmtFull(v) : "-"}
                                  </TableCell>
                                ))}
                                <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 600, color: qr.net_cashflow >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }}>
                                  {fmtFull(qr.net_cashflow)}
                                </TableCell>
                                <TableCell className="data-cell" style={{ textAlign: "right", fontWeight: 700 }}>
                                  {fmtFull(qr.cumulative)}
                                </TableCell>
                              </TableRow>
                            ));
                          })()}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
              {!cashflowData && !cashflowLoading && (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <BarChart3 size={32} style={{ color: "hsl(var(--muted-foreground))", marginBottom: 12 }} />
                    <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14 }}>
                      {scenarioId ? "Click the Cashflow tab to load data" : "Save the scenario first to view cashflow"}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
